import {
  Award,
  CheckCircle2,
  Flame,
  Heart,
  type LucideIcon,
  Shield,
  Target,
  TrendingUp,
  Truck,
} from "lucide-react"

import type { ActivityChip } from "@/types/activity"

/**
 * OKS-548 — Presentational config for an Activity-feed row, derived from
 * the notification `event_key` (KD-4).
 *
 * The shipped `Notifications::Serializer` collapses `event_key` into a
 * frozen 5-value `type` (e.g. `cheer` → `kudo`, `submission.approved` →
 * `mission`). The recognition feed needs finer, color-coded icons, so it
 * derives them here from `event_key` — which is already on the wire —
 * WITHOUT touching that shared serializer contract or the bell panel.
 *
 * Mapping (PRD §3):
 *   kudo → heart (rose) · cheer → flame (orange) ·
 *   submission.approved → check-circle (green) · mission.* → target ·
 *   badge → medallion/award (violet) · rank → blazon/shield (amber, OKS-551)
 *
 * Color is never the sole differentiator — each config carries a distinct
 * icon shape + a localized `labelKey` surfaced as the badge `aria-label`.
 */
export interface ActivityRowConfig {
  icon: LucideIcon
  /** Icon-badge tint (bg + fg), light + dark. */
  badgeClass: string
  /** Filter chip this row reads under. */
  chip: Exclude<ActivityChip, "all">
  /** i18n key for the accessible type label (`activity.type.*`). */
  labelKey: string
}

const KUDO: ActivityRowConfig = {
  icon: Heart,
  badgeClass:
    "bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400",
  chip: "kudos",
  labelKey: "activity.type.kudo",
}

const CHEER: ActivityRowConfig = {
  icon: Flame,
  badgeClass:
    "bg-orange-100 text-orange-600 dark:bg-orange-500/15 dark:text-orange-400",
  chip: "cheers",
  labelKey: "activity.type.cheer",
}

const APPROVED: ActivityRowConfig = {
  icon: CheckCircle2,
  badgeClass:
    "bg-green-100 text-green-600 dark:bg-green-500/15 dark:text-green-400",
  chip: "missions",
  labelKey: "activity.type.approved",
}

const MISSION: ActivityRowConfig = {
  icon: Target,
  badgeClass: "bg-muted text-muted-foreground",
  chip: "missions",
  labelKey: "activity.type.mission",
}

const BADGE: ActivityRowConfig = {
  icon: Award,
  badgeClass:
    "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-400",
  chip: "badges",
  labelKey: "activity.type.badge",
}

// OKS-939 — deferred engagement-bonus rows. `TrendingUp` and not `Award`
// (UX Spec §2): this is PERFORMANCE, and deliberately not the rank-up icon —
// the bonus is the *cause* of a rank change, not the change itself, and reusing
// RANK's icon would conflate them in the feed.
//
// ⚠️ Registering here is NOT optional-but-nice. `activityRowConfig` ends in
// `default: return MISSION`, so an unmapped key renders a plausible generic
// icon rather than failing — the omission is invisible to every appearance test
// and to every user, because it looks like a design choice. That silent
// fallback is why `__tests__/activity-feed.test.ts` asserts this key maps HERE
// and not to the default.
const ENGAGEMENT: ActivityRowConfig = {
  icon: TrendingUp,
  badgeClass:
    "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400",
  chip: "missions",
  labelKey: "activity.type.engagement_bonus",
}

// OKS-551 — rank-up rows are now LIVE. The `creator.rank.up` notification
// (emitted from `BrandRankUpEvent.after_create_commit`) renders here. The
// `Shield` glyph reads as a blazon/crest (the "real blazon" ask), amber-
// tinted, under the Badges chip (PRD §3). The per-tier uploaded blazon
// image is a richer V2 treatment — the feed uses a uniform 32px icon-badge
// grid, so a single Lucide glyph keeps it consistent (UX Spec §7 / PRD Q2).
const RANK: ActivityRowConfig = {
  icon: Shield,
  badgeClass:
    "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400",
  chip: "badges",
  labelKey: "activity.type.rank_up",
}

// OKS-563 — a reward redemption marked shipped. `Truck` matches the
// tracking-line affordance the creator sees on the redemption row itself
// (`TrackingNumberLine`), so the feed row and its destination read as the
// same event.
//
// Chip is `missions` because the feed's chip vocabulary is fixed
// (all / kudos / cheers / missions / badges) and a reward has no home in
// it. That is a known imprecision, not an oversight — widening the chip
// set is a product change, and UX-OQ-4 declined to widen the sibling
// `NotificationType` contract inside this feature ticket for the same
// reason.
//
// ⚠️ Registering here is NOT optional-but-nice — the same trap OKS-939
// documented one config above. `activityRowConfig` ends in
// `default: return MISSION`, so an unmapped `redemption` resource renders
// a plausible generic target icon rather than failing. Nothing reds; it
// just looks like a design choice forever. `__tests__/activity-feed.test.ts`
// asserts this key maps HERE and not to the default.
const REDEMPTION_SHIPPED: ActivityRowConfig = {
  icon: Truck,
  badgeClass: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400",
  chip: "missions",
  labelKey: "activity.type.redemption_shipped",
}

/**
 * Resolve the presentational config for a notification event key.
 *
 * Parses `<audience>.<resource>.<verb>` — the audience prefix is ignored
 * (a cheer is a cheer regardless of recipient), `submission` is split on
 * its verb so `approved` gets the distinct green check. Any unmapped
 * resource falls through to the neutral mission config (mirrors the
 * serializer's `DEFAULT_TYPE`), so this never returns undefined.
 */
export function activityRowConfig(eventKey: string): ActivityRowConfig {
  const [, resource, verb] = eventKey.split(".")

  switch (resource) {
    case "kudo":
      return KUDO
    case "cheer":
      return CHEER
    case "badge":
      return BADGE
    case "rank":
      return RANK
    case "xp":
      return ENGAGEMENT
    case "redemption":
      return REDEMPTION_SHIPPED
    case "submission":
      return verb === "approved" ? APPROVED : MISSION
    default:
      return MISSION
  }
}

/**
 * Whether a row (by event key) reads under the given chip. `all` matches
 * everything; otherwise compares the row's derived chip. Drives the
 * client-side filter (KD-6).
 */
export function matchesChip(eventKey: string, chip: ActivityChip): boolean {
  if (chip === "all") return true
  return activityRowConfig(eventKey).chip === chip
}
